import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  PrinterConnectionConfig,
  PrinterState,
  PrinterEvent,
  IPrinterConnection,
  ConnectionStatus,
} from '@/lib/printerConnection.types'
import { createPrinterAdapter } from '@/lib/printerAdapters'

interface PrinterConnectionState {
  // Stored printer configurations (persisted)
  savedPrinters: PrinterConnectionConfig[]
  
  // Active printer connections and states (runtime only)
  printerStates: Map<string, PrinterState>
  activeConnections: Map<string, IPrinterConnection>
  
  // Event log for monitoring
  eventLog: PrinterEvent[]
  maxEventLogSize: number
  
  // Selected printer for detailed view
  selectedPrinterId: string | null
  
  // Actions
  addPrinter: (config: PrinterConnectionConfig) => void
  updatePrinter: (id: string, config: Partial<PrinterConnectionConfig>) => void
  removePrinter: (id: string) => Promise<void>
  
  connectPrinter: (id: string) => Promise<void>
  disconnectPrinter: (id: string) => Promise<void>
  reconnectPrinter: (id: string) => Promise<void>
  connectAllPrinters: () => Promise<void>
  disconnectAllPrinters: () => Promise<void>
  
  selectPrinter: (id: string | null) => void
  getPrinterState: (id: string) => PrinterState | undefined
  getConnection: (id: string) => IPrinterConnection | undefined
  
  // Print control shortcuts
  startPrint: (printerId: string, filename: string) => Promise<void>
  pausePrint: (printerId: string) => Promise<void>
  resumePrint: (printerId: string) => Promise<void>
  cancelPrint: (printerId: string) => Promise<void>
  
  // Temperature control
  setNozzleTemp: (printerId: string, temp: number) => Promise<void>
  setBedTemp: (printerId: string, temp: number) => Promise<void>
  
  // Internal
  _updatePrinterState: (id: string, state: Partial<PrinterState>) => void
  _addEvent: (event: PrinterEvent) => void
}

export const usePrinterConnectionStore = create<PrinterConnectionState>()(
  persist(
    (set, get) => ({
      savedPrinters: [],
      printerStates: new Map(),
      activeConnections: new Map(),
      eventLog: [],
      maxEventLogSize: 100,
      selectedPrinterId: null,

      addPrinter: (config) => {
        set((state) => ({
          savedPrinters: [...state.savedPrinters, config],
        }))
      },

      updatePrinter: (id, updates) => {
        set((state) => ({
          savedPrinters: state.savedPrinters.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
        }))
      },

      removePrinter: async (id) => {
        const { disconnectPrinter } = get()
        await disconnectPrinter(id)
        
        set((state) => {
          const newStates = new Map(state.printerStates)
          newStates.delete(id)
          return {
            savedPrinters: state.savedPrinters.filter((p) => p.id !== id),
            printerStates: newStates,
            selectedPrinterId: state.selectedPrinterId === id ? null : state.selectedPrinterId,
          }
        })
      },

      connectPrinter: async (id) => {
        const { savedPrinters, activeConnections, _updatePrinterState, _addEvent } = get()
        const config = savedPrinters.find((p) => p.id === id)
        
        if (!config) {
          throw new Error(`Printer ${id} not found`)
        }

        // Check if already connected
        if (activeConnections.has(id)) {
          const existing = activeConnections.get(id)!
          if (existing.getState().connectionStatus === 'connected') {
            return
          }
        }

        _updatePrinterState(id, { connectionStatus: 'connecting' })

        try {
          const adapter = createPrinterAdapter(config)
          
          // Subscribe to events
          adapter.onEvent((event) => {
            _addEvent(event)
            
            // Update state based on events
            if (event.type === 'telemetry' || event.type === 'state_change') {
              const state = adapter.getState()
              _updatePrinterState(id, state)
            }
          })

          await adapter.connect()
          
          set((state) => {
            const newConnections = new Map(state.activeConnections)
            newConnections.set(id, adapter)
            return { activeConnections: newConnections }
          })

          _updatePrinterState(id, adapter.getState())
        } catch (error) {
          _updatePrinterState(id, {
            connectionStatus: 'error',
            lastError: error instanceof Error ? error.message : 'Connection failed',
          })
          throw error
        }
      },

      disconnectPrinter: async (id) => {
        const { activeConnections, _updatePrinterState } = get()
        const connection = activeConnections.get(id)
        
        if (connection) {
          await connection.disconnect()
          
          set((state) => {
            const newConnections = new Map(state.activeConnections)
            newConnections.delete(id)
            return { activeConnections: newConnections }
          })
        }

        _updatePrinterState(id, {
          connectionStatus: 'disconnected',
          printerStatus: 'offline',
        })
      },

      reconnectPrinter: async (id) => {
        const { disconnectPrinter, connectPrinter } = get()
        await disconnectPrinter(id)
        await connectPrinter(id)
      },

      connectAllPrinters: async () => {
        const { savedPrinters, connectPrinter } = get()
        const results = await Promise.allSettled(
          savedPrinters.map((p) => connectPrinter(p.id))
        )
        
        const failed = results.filter((r) => r.status === 'rejected')
        if (failed.length > 0) {
          console.warn(`${failed.length} printer(s) failed to connect`)
        }
      },

      disconnectAllPrinters: async () => {
        const { activeConnections, disconnectPrinter } = get()
        await Promise.all(
          Array.from(activeConnections.keys()).map((id) => disconnectPrinter(id))
        )
      },

      selectPrinter: (id) => {
        set({ selectedPrinterId: id })
      },

      getPrinterState: (id) => {
        return get().printerStates.get(id)
      },

      getConnection: (id) => {
        return get().activeConnections.get(id)
      },

      startPrint: async (printerId, filename) => {
        const connection = get().activeConnections.get(printerId)
        if (!connection) throw new Error('Printer not connected')
        await connection.startPrint(filename)
      },

      pausePrint: async (printerId) => {
        const connection = get().activeConnections.get(printerId)
        if (!connection) throw new Error('Printer not connected')
        await connection.pausePrint()
      },

      resumePrint: async (printerId) => {
        const connection = get().activeConnections.get(printerId)
        if (!connection) throw new Error('Printer not connected')
        await connection.resumePrint()
      },

      cancelPrint: async (printerId) => {
        const connection = get().activeConnections.get(printerId)
        if (!connection) throw new Error('Printer not connected')
        await connection.cancelPrint()
      },

      setNozzleTemp: async (printerId, temp) => {
        const connection = get().activeConnections.get(printerId)
        if (!connection) throw new Error('Printer not connected')
        await connection.setNozzleTemp(temp)
      },

      setBedTemp: async (printerId, temp) => {
        const connection = get().activeConnections.get(printerId)
        if (!connection) throw new Error('Printer not connected')
        await connection.setBedTemp(temp)
      },

      _updatePrinterState: (id, updates) => {
        set((state) => {
          const newStates = new Map(state.printerStates)
          const existing = newStates.get(id) || {
            id,
            config: state.savedPrinters.find((p) => p.id === id)!,
            connectionStatus: 'disconnected' as ConnectionStatus,
            printerStatus: 'offline' as const,
          }
          newStates.set(id, { ...existing, ...updates })
          return { printerStates: newStates }
        })
      },

      _addEvent: (event) => {
        set((state) => {
          const newLog = [event, ...state.eventLog].slice(0, state.maxEventLogSize)
          return { eventLog: newLog }
        })
      },
    }),
    {
      name: 'makerly-printer-connections',
      partialize: (state) => ({
        savedPrinters: state.savedPrinters,
      }),
    }
  )
)

// Hook for subscribing to a specific printer's state
export function usePrinterState(printerId: string | null) {
  const printerStates = usePrinterConnectionStore((s) => s.printerStates)
  if (!printerId) return undefined
  return printerStates.get(printerId)
}

// Hook for getting connection status of all printers
export function useAllPrinterStatuses() {
  const savedPrinters = usePrinterConnectionStore((s) => s.savedPrinters)
  const printerStates = usePrinterConnectionStore((s) => s.printerStates)
  
  return savedPrinters.map((config) => ({
    config,
    state: printerStates.get(config.id),
  }))
}
